const RETURN_STATE_VERSION = 1;
const RETURN_STATE_TTL_MS = 30 * 60 * 1000;
const RETURN_STATE_HISTORY_KEY = '__collaborationProductReturnStateKey';

export interface CollaborationProductReturnState {
  version: typeof RETURN_STATE_VERSION;
  targetKey: string;
  viewportTop?: number;
  savedAt: number;
  documentTimeOrigin?: number;
}

function isPageReloadNavigation() {
  if (typeof window === 'undefined' || typeof window.performance?.getEntriesByType !== 'function') {
    return false;
  }
  const navigationEntry = window.performance.getEntriesByType('navigation').at(0) as
    | PerformanceNavigationTiming
    | undefined;
  return navigationEntry?.type === 'reload';
}

function getDocumentTimeOrigin() {
  const timeOrigin = window.performance?.timeOrigin;
  return typeof timeOrigin === 'number' && Number.isFinite(timeOrigin) ? timeOrigin : undefined;
}

function isReturnStateFromCurrentDocument(storageKey: string) {
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return false;
    const returnState = JSON.parse(raw) as Partial<CollaborationProductReturnState>;
    const documentTimeOrigin = getDocumentTimeOrigin();
    return (
      documentTimeOrigin !== undefined && returnState.documentTimeOrigin === documentTimeOrigin
    );
  } catch {
    return false;
  }
}

export function getCollaborationReturnStorageKey(returnStateKey: string, section: string) {
  return `collaboration:return:${returnStateKey}:${section}`;
}

export function hasCollaborationReturnStateMarker(storageKey: string) {
  if (typeof window === 'undefined') return false;
  const historyState = window.history.state as Record<string, unknown> | null;
  if (historyState?.[RETURN_STATE_HISTORY_KEY] !== storageKey) return false;
  return !isPageReloadNavigation() || isReturnStateFromCurrentDocument(storageKey);
}

export function clearCollaborationReturnState(storageKey: string) {
  try {
    window.sessionStorage.removeItem(storageKey);
    const historyState = window.history.state;
    if (!historyState || typeof historyState !== 'object') return;
    const nextHistoryState = { ...historyState } as Record<string, unknown>;
    delete nextHistoryState[RETURN_STATE_HISTORY_KEY];
    window.history.replaceState(nextHistoryState, document.title, window.location.href);
  } catch {
    // Return-state cleanup must never block rendering or navigation.
  }
}

export function readCollaborationReturnState(
  storageKey: string,
): CollaborationProductReturnState | null {
  if (!hasCollaborationReturnStateMarker(storageKey)) return null;
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CollaborationProductReturnState>;
    if (
      parsed.version !== RETURN_STATE_VERSION ||
      !parsed.targetKey ||
      typeof parsed.savedAt !== 'number' ||
      Date.now() - parsed.savedAt > RETURN_STATE_TTL_MS
    ) {
      clearCollaborationReturnState(storageKey);
      return null;
    }
    return parsed as CollaborationProductReturnState;
  } catch {
    clearCollaborationReturnState(storageKey);
    return null;
  }
}

export function saveCollaborationReturnState(
  storageKey: string,
  targetKey: string,
  viewportTop?: number,
) {
  const returnState: CollaborationProductReturnState = {
    version: RETURN_STATE_VERSION,
    targetKey,
    viewportTop: Number.isFinite(viewportTop) ? viewportTop : undefined,
    savedAt: Date.now(),
    documentTimeOrigin: getDocumentTimeOrigin(),
  };
  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify(returnState));
    const historyState = window.history.state;
    window.history.replaceState(
      {
        ...(historyState && typeof historyState === 'object' ? historyState : {}),
        [RETURN_STATE_HISTORY_KEY]: storageKey,
      },
      document.title,
      window.location.href,
    );
    return true;
  } catch {
    clearCollaborationReturnState(storageKey);
    return false;
  }
}

export function scheduleCollaborationViewportRestore(target: HTMLElement, viewportTop: number) {
  const restore = () => {
    const offset = target.getBoundingClientRect().top - viewportTop;
    if (Math.abs(offset) < 0.5) return;
    window.scrollTo({
      top: Math.max(0, window.scrollY + offset),
      left: 0,
      behavior: 'auto',
    });
  };

  restore();
  let secondFrame = 0;
  const firstFrame = window.requestAnimationFrame(() => {
    restore();
    secondFrame = window.requestAnimationFrame(restore);
  });
  const timer = window.setTimeout(restore, 200);

  return () => {
    window.cancelAnimationFrame(firstFrame);
    if (secondFrame) window.cancelAnimationFrame(secondFrame);
    window.clearTimeout(timer);
  };
}
